import { containerSectionHorizontalPaddingClasses, MlBreadcrumb } from '@curran-catalog/curran-atomic-library'
import singletonRouter from 'next/router'
import { Configure, InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'
import { environment } from '@config/environment'
import React, { useEffect, useMemo, useRef } from 'react'
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs'
import { type IndexUiState, UiState } from 'instantsearch.js'
import { TmPageResultHits } from '@templates/tm-page-results/tm-page-result-hits'
import { TmPageResultsPagination } from '@templates/tm-page-results/tm-page-results-pagination'
import { NoResultsBoundary, VirtualSearchbox } from '@utils/algolia-widgets'
import { useRouter } from 'next/compat/router'
import { ServerState } from '../../pages/page-results'
import { algoliaClient } from '@services/algolia'

export const getSearchRouting = (url?: string) => {
  return {
    router: createInstantSearchRouterNext({
      serverUrl: url,
      singletonRouter,
      routerOptions: { cleanUrlOnDispose: false },
    }),
    stateMapping: {
      stateToRoute(uiState: UiState) {
        const indexUiState: IndexUiState = uiState[environment.algolia.pageLinksIndex] || {}
        return {
          query: indexUiState.query,
          page: indexUiState.page,
        } as UiState
      },
      routeToState(routeState: UiState) {
        return {
          [environment.algolia.pageLinksIndex]: {
            query: routeState.query,
            page: routeState.page,
          },
        } as UiState
      },
    },
  }
}

export const TmPageResults = ({ url, serverState }: ServerState) => {
  const router = useRouter()
  const hitsRef = useRef<HTMLDivElement>(null)
  const scrollTimer = useRef<NodeJS.Timeout | null>(null)

  const scrollToTop = () => {
    scrollTimer.current = setTimeout(() => {
      if (hitsRef.current) {
        hitsRef.current.scrollIntoView(true)
      }
    }, 200)
  }

  useEffect(() => {
    return () => {
      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current)
      }
    }
  }, [])

  const serverResults = useMemo(
    () => serverState?.initialResults[environment.algolia.pageLinksIndex].results[0],
    [serverState?.initialResults],
  )

  return (
    <main
      className={`flex-1 flex flex-col tw-container mx-auto pb-8 lg:pb-40 ${containerSectionHorizontalPaddingClasses}`}
    >
      <div className="py-3">
        <MlBreadcrumb asPath={router?.asPath} />
      </div>

      <div className="flex flex-col">
        <InstantSearchSSRProvider {...serverState}>
          <InstantSearch
            searchClient={algoliaClient}
            indexName={environment.algolia.pageLinksIndex}
            insights={true}
            future={{ preserveSharedStateOnUnmount: false }}
            routing={getSearchRouting(url)}
          >
            <Configure hitsPerPage={10} filters={`sites:${environment.siteDomain}`} />
            <VirtualSearchbox />
            <NoResultsBoundary nbHits={serverResults?.nbHits} searchResultsRedirect="/page-results">
              <TmPageResultHits hitsRef={hitsRef} />
              <TmPageResultsPagination onPageChange={scrollToTop} />
            </NoResultsBoundary>
          </InstantSearch>
        </InstantSearchSSRProvider>
      </div>
    </main>
  )
}
